import React, { useCallback, useEffect, useState } from 'react';
import paymentAccountService from '../../services/paymentAccountService';
import { useToast } from '../Shared/Toast';

const initialForm = {
  bankName: '',
  accountNumber: '',
  title: '',
  isActive: true
};

const initialBonusForm = {
  welcomeBonusAmount: 100,
  onlineTopupBonusPercent: 5
};

const PaymentAccountManagement = () => {
  const { success, error } = useToast();
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [bonusForm, setBonusForm] = useState(initialBonusForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bonusLoading, setBonusLoading] = useState(false);
  const [bonusSaving, setBonusSaving] = useState(false);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await paymentAccountService.getActivePaymentAccounts();
      setAccounts(data || []);
    } catch (err) {
      error('Error', 'Failed to load payment accounts');
    } finally {
      setLoading(false);
    }
  }, [error]);

  const loadBonusSettings = useCallback(async () => {
    setBonusLoading(true);
    try {
      const data = await paymentAccountService.getBonusSettings();
      setBonusForm({
        welcomeBonusAmount: data?.welcomeBonusAmount ?? initialBonusForm.welcomeBonusAmount,
        onlineTopupBonusPercent: data?.onlineTopupBonusPercent ?? initialBonusForm.onlineTopupBonusPercent
      });
    } catch (err) {
      error('Error', 'Failed to load bonus settings');
    } finally {
      setBonusLoading(false);
    }
  }, [error]);

  useEffect(() => {
    loadAccounts();
    loadBonusSettings();
  }, [loadAccounts, loadBonusSettings]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleBonusChange = (e) => {
    const { name, value } = e.target;
    setBonusForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await paymentAccountService.updatePaymentAccount(editingId, form);
        success('Updated', 'Payment account updated successfully');
      } else {
        await paymentAccountService.createPaymentAccount(form);
        success('Created', 'Payment account created successfully');
      }
      resetForm();
      await loadAccounts();
    } catch (err) {
      error('Error', err?.response?.data || 'Failed to save payment account');
    }
  };

  const handleEdit = (account) => {
    setEditingId(account.paymentAccountId);
    setForm({
      bankName: account.bankName || '',
      accountNumber: account.accountNumber || '',
      title: account.title || '',
      isActive: account.isActive ?? true
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment account?')) return;
    try {
      await paymentAccountService.deletePaymentAccount(id);
      success('Deleted', 'Payment account deleted successfully');
      if (editingId === id) resetForm();
      await loadAccounts();
    } catch (err) {
      error('Error', 'Failed to delete payment account');
    }
  };

  const handleBonusSubmit = async (e) => {
    e.preventDefault();
    setBonusSaving(true);
    try {
      const payload = {
        welcomeBonusAmount: Number(bonusForm.welcomeBonusAmount) || 0,
        onlineTopupBonusPercent: Number(bonusForm.onlineTopupBonusPercent) || 0
      };
      const updated = await paymentAccountService.updateBonusSettings(payload);
      setBonusForm({
        welcomeBonusAmount: updated?.welcomeBonusAmount ?? payload.welcomeBonusAmount,
        onlineTopupBonusPercent: updated?.onlineTopupBonusPercent ?? payload.onlineTopupBonusPercent
      });
      success('Updated', 'Bonus settings updated successfully');
    } catch (err) {
      error('Error', err?.response?.data || 'Failed to update bonus settings');
    } finally {
      setBonusSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4">
      <h2 className="text-2xl font-bold mb-4">Payment Account Management</h2>

      <form onSubmit={handleBonusSubmit} className="bg-white border rounded-lg p-4 mb-6 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">Bonus Settings</h3>
            <p className="text-sm text-gray-600">Welcome bonus aur online topup bonus yahan se change ho sakta hai.</p>
          </div>
          {bonusLoading && <span className="text-sm text-gray-500">Loading...</span>}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Welcome Bonus (PKR)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              name="welcomeBonusAmount"
              value={bonusForm.welcomeBonusAmount}
              onChange={handleBonusChange}
              className="w-full border rounded px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">Naye user register hote hi itna balance credit hoga.</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Online Topup Bonus (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              name="onlineTopupBonusPercent"
              value={bonusForm.onlineTopupBonusPercent}
              onChange={handleBonusChange}
              className="w-full border rounded px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">Successful online payment par itna extra bonus add hoga.</p>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={bonusSaving}
            className="bg-emerald-600 text-white px-4 py-2 rounded disabled:opacity-60"
          >
            {bonusSaving ? 'Saving...' : 'Save Bonus Settings'}
          </button>
        </div>
      </form>

      <form onSubmit={handleSubmit} className="bg-white border rounded-lg p-4 mb-6 space-y-3">
        <div>
          <label className="block text-sm font-medium mb-1">Bank Name</label>
          <input
            name="bankName"
            value={form.bankName}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Account Number</label>
          <input
            name="accountNumber"
            value={form.accountNumber}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Account Title</label>
          <input
            name="title"
            value={form.title}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            name="isActive"
            checked={form.isActive}
            onChange={handleChange}
          />
          Active
        </label>
        <div className="flex gap-2">
          <button type="submit" className="bg-indigo-600 text-white px-4 py-2 rounded">
            {editingId ? 'Update Account' : 'Add Account'}
          </button>
          {editingId && (
            <button type="button" onClick={resetForm} className="bg-gray-500 text-white px-4 py-2 rounded">
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      <div className="bg-white border rounded-lg">
        <div className="p-4 border-b font-semibold">Accounts</div>
        {loading ? (
          <div className="p-4">Loading...</div>
        ) : accounts.length === 0 ? (
          <div className="p-4 text-gray-500">No accounts found.</div>
        ) : (
          <div className="divide-y">
            {accounts.map((a) => (
              <div key={a.paymentAccountId} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{a.bankName}</div>
                  <div className="text-sm text-gray-600">{a.accountNumber} {a.title ? `(${a.title})` : ''}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(a)} className="px-3 py-1 rounded bg-blue-600 text-white">Edit</button>
                  <button onClick={() => handleDelete(a.paymentAccountId)} className="px-3 py-1 rounded bg-red-600 text-white">Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentAccountManagement;
